/**
 * This browser's localStorage, or null where it cannot be reached (no
 * window, or a browser that throws on access in a private window or with
 * site data blocked).
 */
export const browserLocalStorage = (): Storage | null => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

export type PerUserDismissedFlag = {
  /** True once this user dismissed the dialog on this browser. */
  isDismissed: (userId: string | null | undefined) => boolean
  /** Remember that this user dismissed the dialog on this browser. */
  remember: (userId: string | null | undefined) => void
}

/**
 * A "this person already dismissed it" flag for a first-time dialog, kept
 * per user so a shared machine still shows the dialog to the next person.
 * The flag for a user is the entry `${keyPrefix}${userId}` holding
 * `dismissedValue`. Without a user id the flag always reads as not
 * dismissed and is never written. Storage that is missing, full or blocked
 * reads as not dismissed and makes a write a no-op, so the dialog simply
 * shows again.
 */
export const perUserDismissedFlag = (
  keyPrefix: string,
  dismissedValue: string,
): PerUserDismissedFlag => ({
  isDismissed: (userId) => {
    if (!userId) return false
    try {
      return (
        browserLocalStorage()?.getItem(`${keyPrefix}${userId}`) ===
        dismissedValue
      )
    } catch {
      return false
    }
  },
  remember: (userId) => {
    if (!userId) return
    try {
      browserLocalStorage()?.setItem(`${keyPrefix}${userId}`, dismissedValue)
    } catch {
      // Storage unavailable: the dialog shows again next time.
    }
  },
})
